'use client';

import { useMemo, useEffect } from 'react';
import CssBaseline from '@mui/material/CssBaseline';
import { AppRouterCacheProvider } from '@mui/material-nextjs/v14-appRouter';
import { Experimental_CssVarsProvider as CssVarsProvider, useColorScheme } from '@mui/material/styles';
import variableTheme from './theme-cssvars';
import { useSettingsContext } from '../components/common/settings/context';
import { updateCoreWithSettings, updateComponentsWithSettings } from './with-settings/update-theme';
import { RTL } from './with-settings/right-to-left';

// Must live inside CssVarsProvider so useColorScheme is available.
// Syncs MUI's internal color scheme with the app's settings on mount and
// whenever settings.colorScheme changes, so the design studio (and any other
// component reading theme.palette.mode) sees the correct mode without requiring
// a manual theme toggle.
function ColorSchemeSync({ colorScheme }) {
  const { setMode } = useColorScheme();
  useEffect(() => {
    if (colorScheme) {
      setMode(colorScheme);
    }
  }, [colorScheme, setMode]);
  return null;
}

export function ThemeProvider({ children }) {
  const contextSettings = useSettingsContext();
  const settings = contextSettings || {};

  useEffect(() => {
    if (settings?.typographyMode) {
      document.body.setAttribute('data-typography-mode', settings.typographyMode);
    }
  }, [settings?.typographyMode]);

  const themeWithSettings = useMemo(() => {
    const updated = updateCoreWithSettings(variableTheme, settings);
    const componentOverrides = updateComponentsWithSettings(settings);
    return {
      ...updated,
      direction: settings.direction || 'ltr',
      components: {
        ...updated.components,
        ...componentOverrides,
      },
    };
  }, [
    settings.contrast,
    settings.direction,
    settings.primaryColor,
    settings.themePalette,
    settings.colorScheme,
    settings.compactLayout, // Add compactLayout to dependencies
    settings.typographyMode,
  ]);

  return (
    <AppRouterCacheProvider options={{ key: 'css' }}>
      <CssVarsProvider theme={themeWithSettings} defaultMode={settings.colorScheme || 'dark'} enableColorScheme>
        <ColorSchemeSync colorScheme={settings.colorScheme} />
        <RTL direction={settings.direction || 'ltr'}>
          <CssBaseline />
          {children}
        </RTL>
      </CssVarsProvider>
    </AppRouterCacheProvider>
  );
}
